import { parameterUtils, type ModelData, type ModelParameter } from '@/shared/stores/model-utils'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

function validateDimensions(width: number, height: number) {
  return {
    width: Math.max(1200, Math.min(3000, width)),
    height: Math.max(1800, Math.min(4500, height)),
  }
}

const totalWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options

  const validated = validateDimensions(numValue, model.height as number)
  const finalWidth = validated.width

  model.width = finalWidth
  updateParam(model, 'top_frame_profile', { width: finalWidth })

  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  const middleVerticalFrame = getParam(model.parameters!, 'middle_vertical_frame_profile')

  const topGlassWidth = finalWidth - (leftFrame?.width || 60) - (rightFrame?.width || 60)
  updateParam(model, 'door_top_glass_panel', { width: topGlassWidth })
  updateParam(model, 'middle_frame_profile', { width: topGlassWidth })

  if (cascadeUpdates) {
    const singleSectionWidth = Math.floor(finalWidth / 2)

    updateParam(model, 'right_frame_profile', {
      position: { x: finalWidth - (rightFrame?.width || 60), y: 0, z: 0 },
    })

    const door2RightFrame = getParam(model.parameters!, 'door_2_right_frame_profile')
    updateParam(model, 'door_2_right_frame_profile', {
      position: {
        ...door2RightFrame?.position,
        x: finalWidth - (rightFrame?.width || 60) - (door2RightFrame?.width || 60),
      } as ModelParameter['position'],
    })

    const door2LeftFrame = getParam(model.parameters!, 'door_2_left_frame_profile')
    updateParam(model, 'door_2_left_frame_profile', {
      position: {
        ...door2LeftFrame?.position,
        x: finalWidth / 2 + (middleVerticalFrame?.width || 60) / 2,
      } as ModelParameter['position'],
    })

    const door2TopFrame = getParam(model.parameters!, 'door_2_top_frame_profile')
    updateParam(model, 'door_2_top_frame_profile', {
      width: singleSectionWidth - (middleVerticalFrame?.width || 60) / 2 - (rightFrame?.width || 60),
      position: {
        ...door2TopFrame?.position,
        x: finalWidth / 2 + (middleVerticalFrame?.width || 60) / 2,
      } as ModelParameter['position'],
    })

    const door2BottomFrame = getParam(model.parameters!, 'door_2_bottom_frame_profile')
    updateParam(model, 'door_2_bottom_frame_profile', {
      width: singleSectionWidth - (middleVerticalFrame?.width || 60) / 2 - (rightFrame?.width || 60),
      position: {
        ...door2BottomFrame?.position,
        x: finalWidth / 2 + (middleVerticalFrame?.width || 60) / 2,
      } as ModelParameter['position'],
    })

    updateParam(model, 'door_1_top_frame_profile', {
      width: singleSectionWidth - (middleVerticalFrame?.width || 60) / 2 - (leftFrame?.width || 60),
    })
    updateParam(model, 'door_1_bottom_frame_profile', {
      width: singleSectionWidth - (middleVerticalFrame?.width || 60) / 2 - (leftFrame?.width || 60),
    })

    const door1RightFrame = getParam(model.parameters!, 'door_1_right_frame_profile')
    updateParam(model, 'door_1_right_frame_profile', {
      position: {
        ...door1RightFrame?.position,
        x: finalWidth / 2 - (middleVerticalFrame?.width || 60) / 2 - (door1RightFrame?.width || 60),
      } as ModelParameter['position'],
    })

    updateParam(model, 'middle_vertical_frame_profile', {
      position: {
        ...middleVerticalFrame?.position,
        x: finalWidth / 2 - (middleVerticalFrame?.width || 60) / 2,
      } as ModelParameter['position'],
    })

    const door1LeftFrame = getParam(model.parameters!, 'door_1_left_frame_profile')
    const leftDoorWidth =
      singleSectionWidth -
      (leftFrame?.width || 60) -
      (door1LeftFrame?.width || 60) -
      (door1RightFrame?.width || 60) -
      (middleVerticalFrame?.width || 60) / 2
    const rightDoorWidth =
      singleSectionWidth -
      (rightFrame?.width || 60) -
      (door2RightFrame?.width || 60) -
      (door2LeftFrame?.width || 60) -
      (middleVerticalFrame?.width || 60) / 2

    updateParam(model, 'door_glass_panel_1', { width: leftDoorWidth })
    const door2GlassPanel = getParam(model.parameters!, 'door_glass_panel_2')
    updateParam(model, 'door_glass_panel_2', {
      width: rightDoorWidth,
      position: {
        ...door2GlassPanel?.position,
        x: singleSectionWidth + (middleVerticalFrame?.width || 60) / 2 + (door2RightFrame?.width || 60),
      } as ModelParameter['position'],
    })

    model.doorConfiguration = {
      ...model.doorConfiguration!,
      totalDoorWidth: finalWidth,
      individualDoorWidth: [leftDoorWidth, rightDoorWidth],
    }
  }

  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options

  const validated = validateDimensions(model.width as number, numValue)
  const finalHeight = validated.height

  model.height = finalHeight
  updateParam(model, 'left_frame_profile', { height: finalHeight })
  updateParam(model, 'right_frame_profile', { height: finalHeight })

  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const middleHorizontalFrame = getParam(model.parameters!, 'middle_frame_profile')
  const doorTopGlassPanel = getParam(model.parameters!, 'door_top_glass_panel')
  const door1TopFrame = getParam(model.parameters!, 'door_1_top_frame_profile')
  const door1BottomFrame = getParam(model.parameters!, 'door_1_bottom_frame_profile')
  const door2BottomFrame = getParam(model.parameters!, 'door_2_bottom_frame_profile')

  const transomHeight =
    (doorTopGlassPanel?.height || 0) + (topFrame?.height || 60) + (middleHorizontalFrame?.height || 60) || 200
  const centerFrameHeight = finalHeight - transomHeight

  updateParam(model, 'middle_vertical_frame_profile', { height: centerFrameHeight })

  if (cascadeUpdates) {
    const newDoorHeight = centerFrameHeight - (door1TopFrame?.height || 60) - (door1BottomFrame?.height || 60)
    updateParam(model, 'door_glass_panel_1', { height: newDoorHeight })
    updateParam(model, 'door_glass_panel_2', { height: newDoorHeight })
    updateParam(model, 'door_1_left_frame_profile', { height: centerFrameHeight })
    updateParam(model, 'door_1_right_frame_profile', { height: centerFrameHeight })
    updateParam(model, 'door_2_left_frame_profile', { height: centerFrameHeight })
    updateParam(model, 'door_2_right_frame_profile', { height: centerFrameHeight })

    const bottomY = finalHeight - (door1BottomFrame?.height || 60)
    updateParam(model, 'door_1_bottom_frame_profile', {
      position: { x: door1BottomFrame?.position?.x || 0, y: bottomY, z: 0 },
    })
    updateParam(model, 'door_2_bottom_frame_profile', {
      position: { x: door2BottomFrame?.position?.x || 0, y: bottomY, z: 0 },
    })

    // Update handle positions (53% of door frame height)
    const rightDoor = getParam(model.parameters!, 'door_glass_panel_2')
    if (rightDoor?.hardware?.handles) {
      const newHandleY = Math.round(centerFrameHeight * 0.53)
      const updatedHandles = rightDoor.hardware.handles.map((handle) => ({
        ...handle,
        position: { ...handle.position, y: newHandleY },
      }))
      updateParam(model, 'door_glass_panel_2', {
        hardware: { ...rightDoor.hardware, handles: updatedHandles },
      })
    }

    // Adjust hinges for door_2_right_frame_profile
    const door2RightFrame = getParam(model.parameters!, 'door_2_right_frame_profile')
    if (door2RightFrame?.hardware?.hinges) {
      const oldFrameHeight = door2RightFrame.height || centerFrameHeight
      const updatedHinges = door2RightFrame.hardware.hinges.map((hinge) => {
        const isBottomHinge1 = hinge.positionId?.includes('bottom_hinge_1')
        const isBottomHinge2 = hinge.positionId?.includes('bottom_hinge_2')
        const relativePosition = oldFrameHeight > 0 ? hinge.position.y / oldFrameHeight : 0
        let newY: number
        if (isBottomHinge1) {
          newY = Math.round(centerFrameHeight * 0.75)
        } else if (isBottomHinge2) {
          newY = Math.round(centerFrameHeight * 0.85)
        } else {
          newY = Math.max(60, Math.min(centerFrameHeight - (hinge.height || 100) - 60, relativePosition * centerFrameHeight))
        }
        return { ...hinge, position: { ...hinge.position, y: newY } }
      })
      updateParam(model, 'door_2_right_frame_profile', {
        hardware: { ...door2RightFrame.hardware, hinges: updatedHinges },
      })
    }

    // Adjust hinges for door_1_left_frame_profile
    const door1LeftFrame = getParam(model.parameters!, 'door_1_left_frame_profile')
    if (door1LeftFrame?.hardware?.hinges) {
      const oldFrameHeight = door1LeftFrame.height || centerFrameHeight
      const updatedHinges = door1LeftFrame.hardware.hinges.map((hinge) => {
        const isBottomHinge1 = hinge.positionId?.includes('bottom_hinge_1')
        const isBottomHinge2 = hinge.positionId?.includes('bottom_hinge_2')
        const relativePosition = oldFrameHeight > 0 ? hinge.position.y / oldFrameHeight : 0
        let newY: number
        if (isBottomHinge1) {
          newY = Math.round(centerFrameHeight * 0.75)
        } else if (isBottomHinge2) {
          newY = Math.round(centerFrameHeight * 0.85)
        } else {
          newY = Math.max(60, Math.min(centerFrameHeight - (hinge.height || 100) - 60, relativePosition * centerFrameHeight))
        }
        return { ...hinge, position: { ...hinge.position, y: newY } }
      })
      updateParam(model, 'door_1_left_frame_profile', {
        hardware: { ...door1LeftFrame.hardware, hinges: updatedHinges },
      })
    }
  }

  return model
}

const transomHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options

  const clampedHeight = Math.max(150, Math.min(800, numValue))
  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const frameHeight = topFrame?.height || 60
  const transomeBottomFrame = getParam(model.parameters!, 'middle_frame_profile')
  const doorTopPanel = getParam(model.parameters!, 'door_top_glass_panel')
  const door1TopFrame = getParam(model.parameters!, 'door_1_top_frame_profile')
  const door1BottomFrame = getParam(model.parameters!, 'door_1_bottom_frame_profile')
  const door2BottomFrame = getParam(model.parameters!, 'door_2_bottom_frame_profile')
  const door1LeftFrame = getParam(model.parameters!, 'door_1_left_frame_profile')
  const door2LeftFrame = getParam(model.parameters!, 'door_2_left_frame_profile')
  const door1RightFrame = getParam(model.parameters!, 'door_1_right_frame_profile')
  const door2RightFrame = getParam(model.parameters!, 'door_2_right_frame_profile')
  const middleVerticalFrame = getParam(model.parameters!, 'middle_vertical_frame_profile')
  const door1GlassPanel = getParam(model.parameters!, 'door_glass_panel_1')
  const door2GlassPanel = getParam(model.parameters!, 'door_glass_panel_2')

  const currentTotalTransomHeight =
    frameHeight + (doorTopPanel?.height || 180) + (transomeBottomFrame?.height || 60)
  const heightDifference = clampedHeight - currentTotalTransomHeight

  const newTotalHeight = (model.height as number) + heightDifference
  model.height = newTotalHeight

  const transomGlassHeight = clampedHeight - frameHeight - (transomeBottomFrame?.height || 60)
  const totalTransomH = clampedHeight
  const middleFrameY = frameHeight + transomGlassHeight
  const doorsY = totalTransomH
  const doorsGlassPanelY = totalTransomH + (door1TopFrame?.height || 60)

  updateParam(model, 'door_top_glass_panel', {
    height: transomGlassHeight,
    position: { x: doorTopPanel?.position?.x || 60, y: frameHeight, z: 0 },
  })

  if (cascadeUpdates) {
    updateParam(model, 'middle_frame_profile', {
      position: { ...transomeBottomFrame?.position, y: middleFrameY, z: 0 } as ModelParameter['position'],
    })

    updateParam(model, 'right_frame_profile', { height: newTotalHeight })
    updateParam(model, 'left_frame_profile', { height: newTotalHeight })

    updateParam(model, 'door_1_top_frame_profile', {
      position: { ...door1TopFrame?.position, y: doorsY, z: 0 } as ModelParameter['position'],
    })
    updateParam(model, 'door_2_top_frame_profile', {
      position: { ...(getParam(model.parameters!, 'door_2_top_frame_profile'))?.position, y: doorsY, z: 0 } as ModelParameter['position'],
    })

    updateParam(model, 'door_glass_panel_1', {
      position: { ...door1GlassPanel?.position, y: doorsGlassPanelY, z: 0 } as ModelParameter['position'],
    })
    updateParam(model, 'door_glass_panel_2', {
      position: { ...door2GlassPanel?.position, y: doorsGlassPanelY, z: 0 } as ModelParameter['position'],
    })

    const centerFrameHeight = newTotalHeight - totalTransomH
    updateParam(model, 'door_1_right_frame_profile', {
      position: { ...door1RightFrame?.position, y: doorsY, z: 0 } as ModelParameter['position'],
      height: centerFrameHeight,
    })
    updateParam(model, 'door_1_left_frame_profile', {
      position: { ...door1LeftFrame?.position, y: doorsY, z: 0 } as ModelParameter['position'],
      height: centerFrameHeight,
    })

    const bottomFrameY = newTotalHeight - (door1BottomFrame?.height || 60)
    updateParam(model, 'door_1_bottom_frame_profile', {
      position: { ...door1BottomFrame?.position, y: bottomFrameY, z: 0 } as ModelParameter['position'],
    })
    updateParam(model, 'door_2_left_frame_profile', {
      position: { ...door2LeftFrame?.position, y: doorsY, z: 0 } as ModelParameter['position'],
      height: centerFrameHeight,
    })
    updateParam(model, 'door_2_right_frame_profile', {
      position: { ...door2RightFrame?.position, y: doorsY, z: 0 } as ModelParameter['position'],
      height: centerFrameHeight,
    })
    updateParam(model, 'door_2_bottom_frame_profile', {
      position: { ...door2BottomFrame?.position, y: bottomFrameY, z: 0 } as ModelParameter['position'],
    })
    updateParam(model, 'middle_vertical_frame_profile', {
      position: { ...middleVerticalFrame?.position, y: doorsY, z: 0 } as ModelParameter['position'],
      height: centerFrameHeight,
    })

    const doorHeight = centerFrameHeight - (door1TopFrame?.height || 60) - (door1BottomFrame?.height || 60)
    updateParam(model, 'door_glass_panel_1', { height: doorHeight })
    updateParam(model, 'door_glass_panel_2', { height: doorHeight })

    const rightDoor = getParam(model.parameters!, 'door_glass_panel_2')
    if (rightDoor?.hardware?.handles) {
      const updatedHandles = rightDoor.hardware.handles.map((handle) => {
        const centerY = doorHeight / 2 - (handle.height || 200) / 2
        return {
          ...handle,
          position: { ...handle.position, y: Math.max(50, Math.min(doorHeight - 250, centerY)) },
        }
      })
      updateParam(model, 'door_glass_panel_2', {
        hardware: { ...rightDoor.hardware, handles: updatedHandles },
      })
    }
  }

  return model
}

const bottomFrame1Width: DimensionHandler = (model, _initial, numValue) => {
  const clampedWidth = Math.max(200, Math.min(2500, numValue))
  const door1BottomFrame = getParam(model.parameters!, 'door_1_bottom_frame_profile')
  const door2BottomFrame = getParam(model.parameters!, 'door_2_bottom_frame_profile')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  const middleVerticalFrame = getParam(model.parameters!, 'middle_vertical_frame_profile')

  const sectionRight =
    (door2BottomFrame?.width || 0) + (middleVerticalFrame?.width || 60) / 2 + (rightFrame?.width || 60) || 810

  const newTotalWidth = clampedWidth + sectionRight
  const door1BottomTopNewWidth =
    clampedWidth - (leftFrame?.width || 60) - (middleVerticalFrame?.width || 60) / 2 || 810

  const minWidth = 1200
  const maxWidth = 3000
  const finalTotalWidth = Math.max(minWidth, Math.min(maxWidth, newTotalWidth))

  model.width = finalTotalWidth

  updateParam(model, 'door_1_top_frame_profile', { width: door1BottomTopNewWidth })
  updateParam(model, 'door_1_bottom_frame_profile', { width: door1BottomTopNewWidth })
  updateParam(model, 'top_frame_profile', { width: finalTotalWidth })

  const topGlassWidth = finalTotalWidth - (leftFrame?.width || 60) - (rightFrame?.width || 60)
  updateParam(model, 'door_top_glass_panel', { width: topGlassWidth })
  updateParam(model, 'middle_frame_profile', { width: topGlassWidth })

  const door1LeftFrameWidth = getParam(model.parameters!, 'door_1_left_frame_profile')?.width || 60
  const door1RightFrameWidth = getParam(model.parameters!, 'door_1_right_frame_profile')?.width || 60
  const door1GlassWidth =
    clampedWidth - door1LeftFrameWidth - door1RightFrameWidth - (leftFrame?.width || 60) - (middleVerticalFrame?.width || 60) / 2
  updateParam(model, 'door_glass_panel_1', { width: door1GlassWidth })

  const door1RightFrameX = clampedWidth - door1LeftFrameWidth - (middleVerticalFrame?.width || 60) / 2
  updateParam(model, 'door_1_right_frame_profile', {
    position: { ...getParam(model.parameters!, 'door_1_right_frame_profile')?.position, x: door1RightFrameX } as ModelParameter['position'],
  })

  const middleFrameX = door1RightFrameX + door1RightFrameWidth
  updateParam(model, 'middle_vertical_frame_profile', {
    position: { ...middleVerticalFrame?.position, x: middleFrameX } as ModelParameter['position'],
  })

  const door2StartX = middleFrameX + 60
  updateParam(model, 'door_2_left_frame_profile', {
    position: { ...getParam(model.parameters!, 'door_2_left_frame_profile')?.position, x: door2StartX } as ModelParameter['position'],
  })
  updateParam(model, 'door_2_top_frame_profile', {
    position: { ...getParam(model.parameters!, 'door_2_top_frame_profile')?.position, x: door2StartX } as ModelParameter['position'],
  })
  updateParam(model, 'door_2_bottom_frame_profile', {
    position: { ...door2BottomFrame?.position, x: door2StartX } as ModelParameter['position'],
  })

  const door2GlassPanelX = door2StartX + (getParam(model.parameters!, 'door_2_left_frame_profile')?.width || 60)
  updateParam(model, 'door_glass_panel_2', {
    position: { ...getParam(model.parameters!, 'door_glass_panel_2')?.position, x: door2GlassPanelX } as ModelParameter['position'],
  })

  updateParam(model, 'right_frame_profile', {
    position: { x: finalTotalWidth - (rightFrame?.width || 60), y: 0, z: 0 },
  })
  updateParam(model, 'door_2_right_frame_profile', {
    position: {
      x: finalTotalWidth - (rightFrame?.width || 60) - (getParam(model.parameters!, 'door_2_right_frame_profile')?.width || 60),
      y: getParam(model.parameters!, 'door_2_right_frame_profile')?.position?.y || 300,
      z: 0,
    },
  })

  model.doorConfiguration = {
    ...model.doorConfiguration!,
    totalDoorWidth: finalTotalWidth,
    leftDoorWidth: clampedWidth,
    individualDoorWidth: [clampedWidth, sectionRight],
  }

  return model
}

const bottomFrame2Width: DimensionHandler = (model, _initial, numValue) => {
  const clampedWidth = Math.max(200, Math.min(2500, numValue))
  const door1BottomFrame = getParam(model.parameters!, 'door_1_bottom_frame_profile')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  const middleVerticalFrame = getParam(model.parameters!, 'middle_vertical_frame_profile')

  const sectionLeft =
    (door1BottomFrame?.width || 0) + (leftFrame?.width || 60) + (middleVerticalFrame?.width || 60) / 2 || 810

  const newTotalWidth = sectionLeft + clampedWidth
  const door2BottomTopNewWidth =
    clampedWidth - (rightFrame?.width || 60) - (middleVerticalFrame?.width || 60) / 2 || 810

  const minWidth = 1200
  const maxWidth = 3000
  const finalTotalWidth = Math.max(minWidth, Math.min(maxWidth, newTotalWidth))

  model.width = finalTotalWidth

  updateParam(model, 'door_2_top_frame_profile', { width: door2BottomTopNewWidth })
  updateParam(model, 'door_2_bottom_frame_profile', { width: door2BottomTopNewWidth })
  updateParam(model, 'top_frame_profile', { width: finalTotalWidth })

  const topGlassWidth = finalTotalWidth - (leftFrame?.width || 60) - (rightFrame?.width || 60)
  updateParam(model, 'door_top_glass_panel', { width: topGlassWidth })
  updateParam(model, 'middle_frame_profile', { width: topGlassWidth })

  const door2LeftFrameWidth = getParam(model.parameters!, 'door_2_left_frame_profile')?.width || 60
  const door2RightFrameWidth = getParam(model.parameters!, 'door_2_right_frame_profile')?.width || 60
  const door2GlassWidth =
    clampedWidth - door2LeftFrameWidth - door2RightFrameWidth - (rightFrame?.width || 60) - (middleVerticalFrame?.width || 60) / 2
  updateParam(model, 'door_glass_panel_2', { width: door2GlassWidth })

  const door2LeftFrameX = sectionLeft + (middleVerticalFrame?.width || 60) / 2
  updateParam(model, 'door_2_left_frame_profile', {
    position: { ...getParam(model.parameters!, 'door_2_left_frame_profile')?.position, x: door2LeftFrameX } as ModelParameter['position'],
  })

  const middleFrameX = sectionLeft - (middleVerticalFrame?.width || 60) / 2
  updateParam(model, 'middle_vertical_frame_profile', {
    position: { ...middleVerticalFrame?.position, x: middleFrameX } as ModelParameter['position'],
  })

  const door2TopBottomStartX = door2LeftFrameX
  updateParam(model, 'door_2_top_frame_profile', {
    position: { ...getParam(model.parameters!, 'door_2_top_frame_profile')?.position, x: door2TopBottomStartX } as ModelParameter['position'],
  })
  updateParam(model, 'door_2_bottom_frame_profile', {
    position: { ...getParam(model.parameters!, 'door_2_bottom_frame_profile')?.position, x: door2TopBottomStartX } as ModelParameter['position'],
  })

  const door2GlassPanelX = door2LeftFrameX + door2LeftFrameWidth
  updateParam(model, 'door_glass_panel_2', {
    position: { ...getParam(model.parameters!, 'door_glass_panel_2')?.position, x: door2GlassPanelX } as ModelParameter['position'],
  })

  updateParam(model, 'right_frame_profile', {
    position: { x: finalTotalWidth - (rightFrame?.width || 60), y: 0, z: 0 },
  })
  updateParam(model, 'door_2_right_frame_profile', {
    position: {
      x: finalTotalWidth - door2RightFrameWidth - (rightFrame?.width || 60),
      y: getParam(model.parameters!, 'door_2_right_frame_profile')?.position?.y || 300,
      z: 0,
    },
  })

  model.doorConfiguration = {
    ...model.doorConfiguration!,
    totalDoorWidth: finalTotalWidth,
    rightDoorWidth: clampedWidth,
    individualDoorWidth: [sectionLeft, clampedWidth],
  }

  return model
}

const doorPanelHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options

  const clampedHeight = Math.max(1200, Math.min(4500, numValue))
  const door1TopFrame = getParam(model.parameters!, 'door_1_top_frame_profile')
  const door1BottomFrame = getParam(model.parameters!, 'door_1_bottom_frame_profile')
  const door2BottomFrame = getParam(model.parameters!, 'door_2_bottom_frame_profile')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const doorTopPanel = getParam(model.parameters!, 'door_top_glass_panel')
  const transomeBottomFrame = getParam(model.parameters!, 'middle_frame_profile')

  const currentDoorPanelHeight = getParam(model.parameters!, 'door_glass_panel_1')?.height || 1790
  const currentDoorSectionHeight =
    (door1TopFrame?.height || 60) + currentDoorPanelHeight + (door1BottomFrame?.height || 60)
  const doorSectionHeightDifference = clampedHeight - currentDoorSectionHeight

  const newTotalHeight = (model.height as number) + doorSectionHeightDifference
  model.height = newTotalHeight

  const standardTopFrameHeight = door1TopFrame?.height || 60
  const standardBottomFrameHeight = door1BottomFrame?.height || 60
  const doorGlassHeight = clampedHeight - standardTopFrameHeight - standardBottomFrameHeight
  const newDoorSectionHeight = clampedHeight

  updateParam(model, 'door_1_top_frame_profile', { height: standardTopFrameHeight })
  updateParam(model, 'door_1_bottom_frame_profile', { height: standardBottomFrameHeight })
  updateParam(model, 'door_2_top_frame_profile', { height: standardTopFrameHeight })
  updateParam(model, 'door_2_bottom_frame_profile', { height: standardBottomFrameHeight })

  updateParam(model, 'door_glass_panel_1', { height: doorGlassHeight })
  updateParam(model, 'door_glass_panel_2', { height: doorGlassHeight })

  if (cascadeUpdates) {
    updateParam(model, 'left_frame_profile', { height: newTotalHeight })
    updateParam(model, 'right_frame_profile', { height: newTotalHeight })

    updateParam(model, 'door_1_left_frame_profile', { height: newDoorSectionHeight })
    updateParam(model, 'door_1_right_frame_profile', { height: newDoorSectionHeight })
    updateParam(model, 'door_2_left_frame_profile', { height: newDoorSectionHeight })
    updateParam(model, 'door_2_right_frame_profile', { height: newDoorSectionHeight })
    updateParam(model, 'middle_vertical_frame_profile', { height: newDoorSectionHeight })

    const bottomFrameY = newTotalHeight - (door1BottomFrame?.height || 60)
    updateParam(model, 'door_1_bottom_frame_profile', {
      position: { ...door1BottomFrame?.position, y: bottomFrameY, z: 0 } as ModelParameter['position'],
    })
    updateParam(model, 'door_2_bottom_frame_profile', {
      position: { ...door2BottomFrame?.position, y: bottomFrameY, z: 0 } as ModelParameter['position'],
    })

    const rightDoor = getParam(model.parameters!, 'door_glass_panel_2')
    if (rightDoor?.hardware?.handles) {
      const updatedHandles = rightDoor.hardware.handles.map((handle) => {
        const centerY = clampedHeight / 2 - (handle.height || 200) / 2
        const newY = Math.max(50, Math.min(clampedHeight - 250, centerY))
        return { ...handle, position: { ...handle.position, y: newY } }
      })
      updateParam(model, 'door_glass_panel_2', {
        hardware: { ...rightDoor.hardware, handles: updatedHandles },
      })
    }
  }

  return model
}

export const model12DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  transomHeight,
  bottomFrame1Width,
  bottomFrame2Width,
  doorPanelHeight,
}
